/* eslint-disable camelcase */
import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { faSpinner } from '@fortawesome/pro-regular-svg-icons/faSpinner';
import { CustomerType } from 'propTypes';
import { captureFunctions, paymentsEnabled } from '../stripe/stripeUtils';
import FA from '../../../utils/FA';
// eslint-disable-next-line import/no-cycle
import { paymentMethodDisplayName, paymentTypes } from './PaymentSelector';
import PaymentDetailsInput from '../detailInputs/PaymentDetailsInput';
import ConvenienceFeeCheckbox from './ConvenienceFeeCheckbox';
import PaymentProfileOptions from '../detailInputs/PaymentProfileOptions';
import PaymentTestModeWarning from './PaymentTestModeWarning';
import NachaMessage from '../NachaMessage';
import rollbar from '../../../utils/rollbarLogger';
import StripeForm from '../StripeForm';
import { filterValues } from '../../../utils';
import { paymentProfiles } from '../../../ducks';
import StripeBankDebitVerify from './StripeBankDebitVerify';
import rollbarLogger from '../../../utils/rollbarLogger';

export const STRIPE_ACSS_DEBIT = 'acss_debit';
export const STRIPE_US_BANK_DEBIT = 'us_bank_account';

/**
 * Payment Input Component for Stripe Bank Debits. Currently supports acss_debit
 * (PADs) and us_bank_account (ACHv2).
 *
 * @see https://stripe.com/docs/payments/bank-debits
 * @param props
 * @return {JSX.Element}
 * @constructor
 */
const StripeBankDebitInput = (props) => {
  const {
    amount,
    billingConfig,
    buttonText,
    customer,
    convenienceFee,
    disabled,
    paymentDetails,
    paymentDetailsChanged,
    requireSaveProfile,
    showSaveProfileOption,
    showAutopayOption,
    submittingText,

    stripePaymentMethod,

    // payment submission functions
    beforeSubmit,
    onSubmit,
    afterSubmit
  } = props;

  const {
    payment_config: {
      payment_methods: {
        [paymentTypes.ACH]: {
          convenience_fee: {
            show_confirmation_message: showConfirmationMessage = false
          } = {}
        } = {}
      } = {}
    } = {},
    ach_payment_gateway: achPaymentGateway,
    ach_payment_info: achPaymentInfo = {}
  } = customer;

  const { test_mode: testMode = false } = achPaymentInfo;
  const renderedButtonText = buttonText(customer, paymentTypes.ACH);
  const renderedSubmitText = submittingText(customer, paymentTypes.ACH);
  const convenienceFeeRequired = showConfirmationMessage && convenienceFee !== 0;
  const [autopay, setAutopay] = useState(true);
  // Initialize as accepted if not required
  const [convenienceFeeAccepted, setConvenienceFeeAccepted] = useState(!convenienceFeeRequired);
  const [errorMessage, setErrorMessage] = useState();
  const [savePaymentProfile, setSavePaymentProfile] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pendingProfile, setPendingProfile] = useState(null);

  const disableSubmit = () => {
    const convenienceFeeOk = convenienceFeeAccepted || !convenienceFeeRequired;
    return !convenienceFeeOk || !paymentDetails.valid || !!disabled;
  };

  const handleError = (error) => {
    if (error.response && error.response.status === 401) {
      // session expired; reload page to trigger login
      window.location.reload();
    } else {
      const message = error.message || 'There was an error processing your payment.';

      rollbarLogger.error("Stripe Bank Debit Input Error", error);

      if (error.code === 'instant_verification') {
        // stripe.js returns an ambiguous error here, we want to hint to people that their credentials are the problem
        setErrorMessage(`${message} Please double-check payment details and try again.`);
      } else if (error.code === 'payment_method_bank_account_blocked') {
        // stripe.js returns a UI message telling customer to talk to stripe support. We want them to talk to our support instead.
        setErrorMessage(`Error: This bank account has failed verification in the past and cannot currently be used. Our support team has been alerted and will work to resolve the issue within 24 hours.  If you are still unable to verify this payment method after that time period, please contact ${customer.name} and supply error code E1000.`);
      }
      else {
        setErrorMessage(`Error: ${message}`);
      }
      setSubmitting(false);
    }
  };

  const beginSubmit = () => {
    if (submitting) {
      throw new Error('duplicate submission');
    } else {
      setErrorMessage(null);
      setSubmitting(true);
    }
  };

  const handleSubmit = (token, { stripe }) => {
    let confirmSetup;
    let confirmPayment;

    switch (stripePaymentMethod) {
      case STRIPE_ACSS_DEBIT:
        confirmSetup = stripe.confirmAcssDebitSetup;
        confirmPayment = stripe.confirmAcssDebitPayment;
        break;
      case STRIPE_US_BANK_DEBIT:
        confirmSetup = async (clientSecret, data) => {
          const {
            payment_method: {
              billing_details: { name, email }
            }
          } = data;
          const payment_data = { billing_details: { name, email } };
          const { setupIntent, error } = await stripe.collectUsBankAccountForSetup(clientSecret, payment_data);

          if (error) {
            return { error };
          } else if (setupIntent.status === 'requires_payment_method') {
            // payment setup was cancelled
            return { error: { message: 'Payment setup was cancelled.' } };
          } else if (setupIntent.status === 'requires_confirmation') {
            return stripe.confirmUsBankAccountSetup(clientSecret);
          }

          return { error: { message: 'An unexpected error occurred. Please try again.' } };
        };
        confirmPayment = async (clientSecret, data) => {
          const {
            payment_method: {
              billing_details: { name, email }
            }
          } = data;
          const payment_data = { billing_details: { name, email } };
          const { paymentIntent, error } = await stripe.collectUsBankAccountForPayment(clientSecret, payment_data);

          if (error) {
            return { error };
          } else if (paymentIntent.status === 'requires_payment_method') {
            // payment was cancelled
            return { error: { message: 'Payment was cancelled.' } };
          } else if (paymentIntent.status === 'requires_confirmation') {
            return stripe.confirmUsBankAccountPayment(clientSecret);
          }

          return { error: { message: 'An unexpected error occurred. Please try again.' } };
        };
        break;
      default:
        confirmSetup = () => false;
        confirmPayment = () => false;
    }

    const extra = {
      ...captureFunctions({
        setupDetails: { autopay, payment_method_types: [stripePaymentMethod] },
        paymentDetails,
        confirmSetup,
        confirmPayment
      }),
      options: {
        autopay,
        stripe_save_profile: savePaymentProfile,
        payment_method_types: [stripePaymentMethod]
      }
    };

    beforeSubmit()
      .then(() => onSubmit({
        savePaymentProfile,
        token: token.id,
        payment_method: paymentTypes.ACH,
        paymentDetails,
        autopay,
        ...extra
      }))
      .then(({ payment_profile, order }) => {
        if (payment_profile
          && payment_profile.payment_verification
          && payment_profile.payment_verification.status !== 'verified'
        ) {
          setPendingProfile(payment_profile);
        } else {
          afterSubmit({ payment_profile, order });
        }
      })
      .catch(handleError)
      .then(() => setSubmitting(false));
  };

  if (!paymentsEnabled(achPaymentGateway, achPaymentInfo)) {
    return (
      <div className='alert alert-danger'>
        <FA name='warning' nbsp />
        {
          `
          ${paymentMethodDisplayName(customer, paymentTypes.ACH)} payments have not been configured.
          Please contact the system administrator.
          `
        }
      </div>
    );
  }

  if (pendingProfile) {
    return <StripeBankDebitVerify paymentProfile={pendingProfile} />;
  }

  return (
    <StripeForm
      beforeSubmit={beginSubmit}
      onSubmit={handleSubmit}
      onError={handleError}
      details={filterValues(paymentDetails)}
      usesIntents
    >
      {errorMessage && <div className='alert alert-danger'>{errorMessage}</div>}

      <PaymentTestModeWarning testMode={testMode} />

      <fieldset className='payment-ach-field'>
        <PaymentDetailsInput onChange={paymentDetailsChanged} customer={customer} includeEmail disabled={submitting} />
      </fieldset>

      {convenienceFeeRequired && (
        <ConvenienceFeeCheckbox
          customer={customer}
          checked={convenienceFeeAccepted}
          paymentMethod={paymentTypes.ACH}
          onChange={() => setConvenienceFeeAccepted(!convenienceFeeAccepted)}
          disabled={submitting}
        />
      )}

      <PaymentProfileOptions
        billingConfig={billingConfig}
        customer={customer}
        showSaveProfileOption={showSaveProfileOption}
        showAutopayOption={showAutopayOption}
        savePaymentProfile={savePaymentProfile}
        requireSaveProfile={requireSaveProfile}
        paymentMethod={paymentTypes.ACH}
        autopay={autopay}
        onChange={(options) => {
          setAutopay(options.autopay);
          setSavePaymentProfile(options.savePaymentProfile);
        }}
        disabled={submitting}
      />

      {submitting ? (
        <button className='btn btn-primary' type='button' disabled>
          <FA icon={faSpinner} spin nbsp />
          {renderedSubmitText}
        </button>
      ) : (
        <button
          className='btn btn-primary'
          type='submit'
          disabled={disableSubmit()}
        >
          {`Select Bank and ${renderedButtonText}`}
        </button>
      )}

      <NachaMessage
        amount={amount}
        buttonText={renderedButtonText}
        customer={customer}
      />
    </StripeForm>
  );
};

StripeBankDebitInput.propTypes = {
  customer: CustomerType.isRequired,
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  billingConfig: PropTypes.shape({}).isRequired,
  buttonText: PropTypes.func,
  convenienceFee: PropTypes.number,
  disabled: PropTypes.bool,
  paymentDetails: PropTypes.shape({
    valid: PropTypes.bool
  }).isRequired,
  paymentDetailsChanged: PropTypes.func,
  requireSaveProfile: PropTypes.bool,
  showSaveProfileOption: PropTypes.bool,
  submittingText: PropTypes.func,

  stripePaymentMethod: PropTypes.oneOf([STRIPE_ACSS_DEBIT, STRIPE_US_BANK_DEBIT]).isRequired,

  beforeSubmit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  afterSubmit: PropTypes.func.isRequired
};

StripeBankDebitInput.defaultProps = {
  amount: '0.00',
  buttonText: () => paymentTypes.ACH,
  convenienceFee: 0,
  disabled: false,
  paymentDetailsChanged: () => false,
  requireSaveProfile: false,
  showSaveProfileOption: true,
  submittingText: () => 'Submitting...'
};

const mapDispatchToProps = (dispatch) => ({
  refreshPaymentProfiles: () => dispatch(paymentProfiles.actions.markDirty())
});

export default connect(null, mapDispatchToProps)(StripeBankDebitInput);
